<template>
    <div v-if="Object.keys(content).length > 0" class="rich-text">
        <RichTextRenderer
            :document="content"
            :node-renderers="renderNodes"
        />
    </div>
</template>
<script setup>
import { computed, useNuxtApp, resolveComponent } from '#imports';
import { h } from 'vue';
import RichTextRenderer from '~dsc/utils/contentful-rich-text';
import { BLOCKS, INLINES } from '~dsc/utils/contentful-types';

const { $cms } = useNuxtApp();

const props = defineProps({
    content: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    hideSomeIcons: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const renderNodes = computed(() => ({
    [BLOCKS.EMBEDDED_ASSET]: renderImage,
    [INLINES.ENTRY_HYPERLINK]: renderLink,
    [INLINES.HYPERLINK]: renderLink,
    [BLOCKS.HEADING_1]: renderHeading,
    [BLOCKS.HEADING_2]: renderHeading,
    [BLOCKS.HEADING_3]: renderHeading,
    [BLOCKS.HEADING_4]: renderHeading,
    [BLOCKS.HEADING_5]: renderHeading,
    [BLOCKS.HEADING_6]: renderHeading,
    [BLOCKS.DOCUMENT]: renderOther,
    [BLOCKS.PARAGRAPH]: renderOther,
    [BLOCKS.OL_LIST]: renderOther,
    [BLOCKS.UL_LIST]: renderOther,
    [BLOCKS.LIST_ITEM]: renderOther,
    [BLOCKS.QUOTE]: renderOther,
}));

const renderLink = node => {
    let data = {};
    const nuxtLink = resolveComponent('NuxtLink');
    switch (node.nodeType) {
        case 'hyperlink':
            if (node.data.uri.match(/^http(s?):\/\//) === null
                && node.data.uri.match(/^mailto:/) === null
                && node.data.uri.match(/^tel:/) === null
            ) {
                data = {
                    to: node.data.uri,
                    external: true,
                };
            } else if (node.data.uri.match(/^https:\/\/(www.?)facebook.com/)
                || node.data.uri.match(/^https:\/\/(www.?)instagram.com/)
                || node.data.uri.match(/^https:\/\/(www.?)youtube.com/)
                || node.data.uri.match(/^https:\/\/(www.?)tiktok.com/)
            ) {
                const [, address] = node.data.uri.split('//');
                const [some] = address.replace('www.', '').split('.');
                data = {
                    some: `${some}_black`,
                    href: node.data.uri,
                    external: true,
                };
            } else {
                data = {
                    href: node.data.uri,
                    external: true,
                };
            }
        break;

        default:
        case 'entry-hyperlink':
            data = $cms.parseLink({
                url: node.data.target,
            });
        break;
    }

    if (!props.hideSomeIcons && data?.some) {
        const icon = h('DSIcon', { props: { code: data.some, size: 'xl' } });
        try {
            return h(nuxtLink, { to: data.to }, [icon, ...node.content.map(item => renderItem(item))]);
        } catch (e) {
            return '<linking failed>';
        }
    }

    try {
        if (data.to) {
            return h(nuxtLink, { ...data }, () => [...node.content.map(item => renderItem(item))]);
        }

        return h('a', { ...data }, node.content.map(item => renderItem(item)));
    } catch (e) {
        return `<linking failed (${e})>`;
    }
};

const renderImage = node => {
    const { url } = node.data.target;

    if (/^\/\/assets/.test(url)) {
        // The node is not an image, so show a link
        return h('a', { href: url, target: '_blank' }, () => node.data.target.title);
    }

    return h('img', { src: node.data.target.url, alt: node.data.target.title || '' });
};

const renderHeading = node => {
    const getClassName = () => {

        switch (node.nodeType) {
        case 'heading-1':
            return 'h1';
        case 'heading-2':
            return 'h2';
        case 'heading-3':
            return 'h3';
        case 'heading-4':
            return 'h4';
        case 'heading-5':
            return 'h5';
        case 'heading-6':
            return 'h6';
        default:
            return 'h1';
        }
    };

    const className = getClassName();

    return h(className, { class: className }, node.content.map(item => renderItem(item)));
};

const renderOther = node => {
    const getElementType = () => {
        switch (node.nodeType) {
            case BLOCKS.PARAGRAPH:
                return 'p';
            case BLOCKS.OL_LIST:
                return 'ol';
            case BLOCKS.UL_LIST:
                return 'ul';
            case BLOCKS.LIST_ITEM:
                return 'li';
            case BLOCKS.QUOTE:
                return 'blockquote';
            case BLOCKS.DOCUMENT:
                return 'div';

            default:
                return 'div';
        }
    };

    const elementType = getElementType();

    return h(elementType, {}, node.content.map(item => renderItem(item)));
};

const renderText = node => {
    if (!node.marks.length) {
        return node.value;
    } else if (node.marks[0] && node.marks[0].type) {
        // eslint-disable-next-line prefer-destructuring
        const type = node.marks[0].type;

        if (type === 'bold') {
            return h('strong', {}, node.value);
        } else if (type === 'italic') {
            return h('em', {}, node.value);
        } else if (type === 'underline') {
            return h('u', {}, node.value);
        } else if (type === 'subscript') {
            return h('sub', {}, node.value);
        } else if (type === 'superscript') {
            return h('sup', {}, node.value);
        }

        return node.value;
    }

    return node.value;
};

const renderItem = node => {
    const isText = node.nodeType === 'text';
    const isImage = node.nodeType === BLOCKS.EMBEDDED_ASSET;
    const isLink = [INLINES.HYPERLINK, INLINES.ENTRY_HYPERLINK].includes(node.nodeType);
    const isHeading = [BLOCKS.HEADING_1, BLOCKS.HEADING_2, BLOCKS.HEADING_3, BLOCKS.HEADING_4, BLOCKS.HEADING_5, BLOCKS.HEADING_6].includes(node.nodeType);
    const isOther = [BLOCKS.DOCUMENT, BLOCKS.PARAGRAPH, BLOCKS.OL_LIST, BLOCKS.UL_LIST, BLOCKS.LIST_ITEM, BLOCKS.QUOTE].includes(node.nodeType);

    if (isText) {
        return renderText(node);
    } else if (isLink) {
        return renderLink(node);
    } else if (isImage) {
        return renderImage(node);
    } else if (isHeading) {
        return renderHeading(node);
    } else if (isOther) {
        return renderOther(node);
    }

    return node.value;
};
</script>
<script>
export default {
    name: 'CFText',
};
</script>
<style scoped>
a {
  font-weight: 700;
  color: var(--color-primary-700);
  text-decoration: underline;
}

p {
  white-space: break-spaces;
}

.rich-text {
  word-break: break-word;
}
.rich-text .icon {
  margin: 1rem 1rem 0 0;
}
</style>
